import { ref, onUnmounted } from 'vue'
import { useMainStore } from '@/stores/main'
import * as Server from '@/services/Server'
import * as Auth from '@/services/Auth'

/**
 * Signed link to enter the game.
 *
 * The launch token lives 60 seconds (TOKEN_MAX_AGE_SECONDS on the game server), so the link must be
 * kept fresh while at least one button is on the page: the request is shared (one call, one timer)
 * between every component using `useGameLink()`. The interval stays under the token life, so the
 * link is always valid when it is clicked.
 *
 * The same answer carries the player count (from a cache on the server, see GamePresenceLib): the
 * play page reads it from here, so refreshing the link is the only call an open page makes.
 */
const REFRESH_MS = 30000

const url = ref(null)
const players = ref(null)
const loading = ref(false)

let subscribers = 0
let timer = null

async function refresh() {
  // An account that has not been activated yet has no way in: the server refuses to sign the link
  // (see GameServerController::launch), so there is no point in asking for it.
  if (!Auth.isVerified()) {
    url.value = null
    return
  }

  loading.value = true

  try {
    const store = useMainStore()
    const response = await Server.get(store.apiPath.game_launch)
    const { url: base, token, players: count } = response.data ?? {}
    url.value = base && token ? `${base}?token=${encodeURIComponent(token)}` : null
    // null = the game server could not be asked: keep the count on screen instead of showing 0.
    if (count !== null && count !== undefined) players.value = count
  } catch {
    url.value = null
  } finally {
    loading.value = false
  }
}

// Browsers throttle timers in background tabs: refresh as soon as the tab is visible again.
function refreshIfVisible() {
  if (document.visibilityState === 'visible') refresh()
}

function start() {
  refresh()
  timer = setInterval(refresh, REFRESH_MS)
  document.addEventListener('visibilitychange', refreshIfVisible)
}

function stop() {
  clearInterval(timer)
  timer = null
  document.removeEventListener('visibilitychange', refreshIfVisible)
}

/** Exposes the launch link, keeping it fresh while the calling component stays mounted. */
export function useGameLink() {
  if (subscribers++ === 0) start()

  onUnmounted(() => {
    if (--subscribers === 0) stop()
  })

  return { url, players, loading }
}

/**
 * Refetch the link right away: a component that just changed something the launch token carries
 * (an event activation, see EventActivationButton) calls this, so the next Play click carries the
 * new value instead of waiting up to a refresh for the timer. Harmless with no button on the
 * page: it only updates the cached link.
 */
export function refreshGameLink() {
  return refresh()
}
